import go, { Diagram } from 'gojs'
import { ReactDiagram } from 'gojs-react'
import { RefObject } from 'react'

import { addDiagramNode } from '@/components/Diagram/handlers'
import { getModelAsObject } from '@/components/Diagram/utils'
import {
	changeUserPermission,
	createChildNode,
	createNode,
	createNodeLock,
	deleteNode as deleteStructure,
	deleteNodeLock,
	deleteUserPermission,
	getNodesOfSpecificParent,
	getRootNodes,
} from '@/endpoints'
import {
	NewStructureDto,
	NewStructureUserPermissionDto,
	StructureObjectDto as StructureObjectDtoOld,
	StructureUserPermissionDto,
} from '@/endpoints/models'
import { DiagramTemplateType } from '@/enums'
import { StructureDto, StructureObjectDto } from '@/rtkApi/mddApiSliceGenerated'
import { RootState } from '@/store'
import { apiCallAction, AppDispatch } from '@/store/utils'
import diagramColors from '@/styles/diagramColors'
import { isEditableNodeType } from '@/utils/nodes'

import { closeTabAndRelated, openTab } from '../tab/actions'
import { NodeSession } from '.'
import { isRootStructureParent } from './helpers'
import {
	NODE_ADD,
	NODE_ADD_SYSTEM,
	NODE_COLLAPSE,
	NODE_DELETE,
	NODE_DESELECT,
	NODE_EXPAND,
	NODE_IMPORT_STATE,
	NODE_LOAD_CHILDREN,
	NODE_LOAD_NESTED_CHILDREN,
	NODE_LOAD_PERMISSIONS,
	NODE_LOAD_SYSTEM,
	NODE_LOCK,
	NODE_REFRESH_TREE,
	NODE_SELECT,
	NODE_SELECT_MODE_CHANGE,
	NODE_SET_SELECTED,
	NODE_UNLOCK,
} from './constants'
import { LoadNode, LoadNodeHistoryVersion } from './general-actions'

interface LoadSystemNodes {
	payload: StructureObjectDto[]
	type: typeof NODE_LOAD_SYSTEM
}

interface LoadNodeChildren {
	metadata: {
		parentId: number
	}
	payload: StructureObjectDto[]
	type: typeof NODE_LOAD_CHILDREN
}

interface LoadNestedNodeChildren {
	payload: StructureDto[]
	type: typeof NODE_LOAD_NESTED_CHILDREN
}

interface AddNode {
	payload: StructureDto
	type: typeof NODE_ADD | typeof NODE_ADD_SYSTEM
}

interface ExpandNode {
	key: number
	type: typeof NODE_EXPAND
}

interface CollapseNode {
	key: number
	type: typeof NODE_COLLAPSE
}

interface DeleteNode {
	metadata: {
		node: StructureDto
	}
	payload: void
	type: typeof NODE_DELETE
}

interface LockNode {
	metadata: {
		node: StructureDto
		userId: number
		userName: string
	}
	payload: void
	type: typeof NODE_LOCK
}

interface UnlockNode {
	metadata: {
		node: StructureDto
	}
	payload: void
	type: typeof NODE_UNLOCK
}

interface NodeImportState {
	session: NodeSession
	type: typeof NODE_IMPORT_STATE
}

interface NodeLoadPermissions {
	metadata: {
		nodeId: number
	}
	payload: StructureUserPermissionDto[]
	type: typeof NODE_LOAD_PERMISSIONS
}

interface NodeRefreshTree {
	type: typeof NODE_REFRESH_TREE
}

interface SelectNodeModeChange {
	metadata: {
		selectMode: boolean
	}
	type: typeof NODE_SELECT_MODE_CHANGE
}
interface SelectNodes {
	metadata: {
		selectedNodes: number[]
	}
	type: typeof NODE_SELECT
}

interface DeselectNodes {
	metadata: {
		deselectedNodes: number[]
	}
	type: typeof NODE_DESELECT
}

interface SetSelectedNodes {
	metadata: {
		selectedNodes: number[]
	}
	type: typeof NODE_SET_SELECTED
}

export const loadSystemNodes = () =>
	apiCallAction<LoadSystemNodes>(() => getRootNodes(), NODE_LOAD_SYSTEM)

export const loadNodeChildren = (id: number) =>
	apiCallAction<LoadNodeChildren>(
		() => getNodesOfSpecificParent(id),
		NODE_LOAD_CHILDREN,
		{ parentId: id },
	)

export const loadNestedNodeChildren =
	(id: number) => async (dispatch: AppDispatch) => {
		const children = await dispatch(
			apiCallAction<LoadNestedNodeChildren>(
				() => getNodesOfSpecificParent(id, { recursive: true }),
				NODE_LOAD_NESTED_CHILDREN,
			),
		)

		return children
	}

export const selectNode =
	(node: StructureDto) =>
	async (dispatch: AppDispatch, getValues: () => RootState) => {
		const selectMode = getValues().node.selectMode

		if (!selectMode) {
			dispatch(selectModeChange(true))
		}

		dispatch({
			type: NODE_SELECT,
			metadata: {
				selectedNodes: [node.id],
			},
		})
	}

export const selectAllNode =
	(node: StructureDto) =>
	async (dispatch: AppDispatch, getValues: () => RootState) => {
		const selectMode = getValues().node.selectMode

		if (!node.id) return

		const isFolder =
			getValues().node.nodes[node.id]?.type ===
			StructureObjectDtoOld.TypeEnum.FOLDER

		if (!selectMode) {
			dispatch(selectModeChange(true))
		}

		if (isFolder) {
			const nodesToSelect = await dispatch(loadNestedNodeChildren(node.id))
			const nestedNodeIds = nodesToSelect.map((n) => n.id)

			dispatch({
				type: NODE_SELECT,
				metadata: {
					selectedNodes: [node.id, ...nestedNodeIds],
				},
			})
		}

		dispatch({
			type: NODE_SELECT,
			metadata: {
				selectedNodes: [node.id],
			},
		})
	}

export const deselectNodes =
	(nodeIds: number[]) => async (dispatch: AppDispatch) => {
		dispatch({
			type: NODE_DESELECT,
			metadata: {
				deselectedNodes: nodeIds,
			},
		})
	}

export const deselectAllNode =
	(node: StructureDto) =>
	async (dispatch: AppDispatch, getValues: () => RootState) => {
		const { children } = getValues().node

		if (!node.id) return

		const getChildren = (parentId: number) => {
			let result = [parentId]
			const parentChildren = children[parentId]

			if (!parentChildren) {
				return []
			}

			result = [...result, ...parentChildren]

			parentChildren?.forEach((child) => {
				const ch = getChildren(child)

				if (ch) {
					result = [...result, ...ch]
				}
			})

			return result
		}

		const nodeToDeselect = getChildren(node.id)

		dispatch({
			type: NODE_DESELECT,
			metadata: {
				deselectedNodes: [...nodeToDeselect, node.id],
			},
		})
	}

export const selectModeChange =
	(selectMode: boolean) => async (dispatch: AppDispatch) =>
		dispatch({
			type: NODE_SELECT_MODE_CHANGE,
			metadata: {
				selectMode,
			},
		})

export const addNode = (parentId: number, data: NewStructureDto) => {
	return async (dispatch: AppDispatch) => {
		const newNode = await dispatch(
			apiCallAction<AddNode>(() => createChildNode(parentId, data), NODE_ADD),
		)

		const isEditable = isEditableNodeType(data.type)

		dispatch(loadNodeChildren(parentId))
		dispatch(expandNode(parentId))

		if (isEditable) {
			await dispatch(lockNode(newNode))
		}

		dispatch(openTab(newNode, false, isEditable))
	}
}

export const addNodeInDiagramContextMenu = (
	parentId: number,
	data: NewStructureDto,
	diagramProps?: {
		diagramRef: RefObject<ReactDiagram> | undefined
		isDiagramAddModal?: boolean
		node?: StructureDto
	},
) => {
	const { diagramRef } = diagramProps || {}
	return async (dispatch: AppDispatch) => {
		const newNode = await dispatch(
			apiCallAction<AddNode>(() => createChildNode(parentId, data), NODE_ADD),
		)

		const instanceDiagram = diagramRef?.current?.getDiagram() as Diagram
		const model = instanceDiagram?.model as go.GraphLinksModel
		const parsedModel = getModelAsObject(instanceDiagram)

		const nodePayload = {
			key: newNode?.id || -1,
			category: DiagramTemplateType.Table,
			text: newNode.name || '',
			code: newNode.code || '',
			loc: '',
			tableColumnsVisibility: true,
			nodeBodyColor: diagramColors.node.body,
			nodeHeaderColor: diagramColors.node.header,
			version: 1,
		}

		addDiagramNode({
			instanceDiagram,
			model,
			nodePayload,
			parsedModel,
		})
	}
}

export const addSystemNode =
	(data: NewStructureDto) => async (dispatch: AppDispatch) => {
		const newSystemNode = await dispatch(
			apiCallAction<AddNode>(() => createNode(data), NODE_ADD_SYSTEM),
		)

		dispatch(loadSystemNodes())
		dispatch(openTab(newSystemNode, false))
	}

const reloadTreeAfterNodeChange =
	(node: StructureDto) =>
	(dispatch: AppDispatch, getValues: () => RootState) => {
		if (node.type === StructureObjectDtoOld.TypeEnum.SYSTEM) {
			dispatch(loadSystemNodes())
			return
		}

		const parentId = node.parentStructureId
		if (
			isRootStructureParent(parentId, getValues().node.nodes) ||
			parentId == null
		) {
			dispatch(loadSystemNodes())
			return
		}

		dispatch(loadNodeChildren(parentId))
	}

export const deleteNode =
	(node: StructureDto) =>
	async (dispatch: AppDispatch, getValues: () => RootState) => {
		dispatch(closeTabAndRelated(node))

		await dispatch(
			apiCallAction<DeleteNode>(
				() => deleteStructure(node.id || -1),
				NODE_DELETE,
				{
					node,
				},
			),
		)

		reloadTreeAfterNodeChange(node)(dispatch, getValues)
	}

export const lockNode =
	(node: StructureDto) =>
	async (dispatch: AppDispatch, getValues: () => RootState) => {
		const user = getValues().auth.user

		if (!user) {
			throw new Error(`There is no authenticated user`)
		}

		await dispatch(
			apiCallAction<LockNode>(() => createNodeLock(node.id || -1), NODE_LOCK, {
				node,
				userId: user.id,
				userName: user.compositeName,
			}),
		)

		reloadTreeAfterNodeChange(node)(dispatch, getValues)
	}

export const unlockNode =
	(node: StructureDto) =>
	async (dispatch: AppDispatch, getValues: () => RootState) => {
		await dispatch(
			apiCallAction<UnlockNode>(
				() => deleteNodeLock(node.id || -1),
				NODE_UNLOCK,
				{
					node,
				},
			),
		)

		reloadTreeAfterNodeChange(node)(dispatch, getValues)
	}

export const expandNode = (key: number): ExpandNode => ({
	type: NODE_EXPAND,
	key,
})

export const collapseNode = (key: number): CollapseNode => ({
	type: NODE_COLLAPSE,
	key,
})

export const loadNodeState =
	(session: NodeSession) =>
	(dispatch: AppDispatch) => {
		const updated = { ...session }

		// Normalize expanded nodes
		const expanded = new Set<number>()
		session.expanded.forEach((id) => expanded.add(id))
		updated.expanded = Array.from(expanded)

		dispatch({
			type: NODE_IMPORT_STATE,
			session: updated,
		})

		// Children loading is handled by TreeV2 via V2 API
		// TreeV2's useTreeExpansion hook will load children for expanded items
		return Promise.resolve()
	}

export const setNodePermission = (
	nodeId: number,
	userId: number,
	permissionCode: NewStructureUserPermissionDto.PermissionCodeEnum,
) =>
	apiCallAction(() => changeUserPermission({ permissionCode }, nodeId, userId))

export const removeNodePermission = (nodeId: number, userId: number) =>
	apiCallAction(() => deleteUserPermission(nodeId, userId))

export const nodeRefreshTree =
	() => async (dispatch: AppDispatch, getValues: () => RootState) => {
		await dispatch(loadSystemNodes())

		await Promise.all(
			getValues().node.expanded.map(async (nodeId) => {
				try {
					await dispatch(loadNodeChildren(nodeId))
				} catch (e) {
					console.warn(`Failed to reload children for ${nodeId}:`, e)
				}
			}),
		)
	}

export type Actions =
	| LoadSystemNodes
	| LoadNodeChildren
	| LoadNestedNodeChildren
	| AddNode
	| ExpandNode
	| CollapseNode
	| DeleteNode
	| LockNode
	| UnlockNode
	| NodeImportState
	| LoadNode
	| NodeLoadPermissions
	| NodeRefreshTree
	| SelectNodes
	| DeselectNodes
	| SetSelectedNodes
	| SelectNodeModeChange
	| LoadNodeHistoryVersion
